import cdnAsset_RXDuDN1V from "@/assets/cdn/png/RXDuDN1V.png";
import cdnAsset_BbVwPaWC from "@/assets/cdn/svg/BbVwPaWC.svg";
import cdnAsset_C29CXa4K from "@/assets/cdn/svg/C29CXa4K.svg";
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Popup as AntdMobilePopup } from "antd-mobile";
import { Space } from "antd-mobile";
import { Button } from "../Base/Button";
import { Tabs, Tab } from "../Base/Tabs";
import { Form, FormItem } from "../Base/Form";
import { Checkbox } from "../Base/Checkbox";
import { AntdLink } from "../Base/AntdTypography";
import { Toast } from "../Base/Toast";
import Turnstile from "../Turnstile";
import { triggerAgreementConfirmation } from "../AgreementConfirmation";
import { ProtocolDialog } from "../ProtocolDialog";
import { EnhancedInput } from "../Base/Input/EnhancedInput";
import { ImageCode } from "../Base/ImageCode";
import CountryCodeSelect from "../Base/CountrySelect/CountryCodeSelect";
import { useUserAuth, useUserStore } from "../../modules";
import { useStartupDataFlattened } from "../../modules/startup";
import {
  LoginRequest,
  RegisterRequest,
} from "../../modules/user/types/auth.types";
import { useCountdown } from "../../hooks/useUtils";
import { sendSmsCode } from "../../api/sms";
import { getAuthConfig } from "../../api/auth";
import {
  processPhone,
  getPhonePattern,
  validatePhone,
} from "../../utils/phoneValidation";
import { useAuthConfigStore } from "../../stores";
import utils from "../../utils/utils";
import { wsUtils } from "../../utils/wsUtils";
import {
  isWebPushSupported,
  subscribeHasLogin,
  getPushSubscriptionStatus,
} from "../../utils/webPush";
import { useT } from "../../i18n/hooks";
import {
  useLoginRegisterConfig,
  useResetPasswordIpInCooldown,
  useResetPasswordEmailIpInCooldown,
  useOnlineServiceSupportUrl,
} from "../../modules/homeDisplay";
import { useHomeDisplayStore } from "../../modules/homeDisplay/stores";
import { ResetPasswordDialog } from "../../components/ResetPasswordDialog";
import { ResetPasswordByEmailDialog } from "../../components/ResetPasswordByEmailDialog";
import { PasswordRecoveryDialog } from "../../components/PasswordRecoveryDialog";
import { TooManyPasswordAttemptsDialog } from "../../components/TooManyPasswordAttemptsDialog";
import trackingService from "@/services/trackingService";
import { AdUtils } from "../../utils";
import { useNavigation, ROUTES } from "../../utils/navigation";
import { usePopupQueue } from "../../contexts";
import {
  LOGIN_REGISTER_POPUP_EVENT,
  CLOSE_LOGIN_REGISTER_POPUP_EVENT,
  consumePendingLoginRegisterPopup,
  releaseLoginRegisterPopupListener,
} from "./triggerLoginRegisterPopup";
import type {
  LoginRegisterPopupProps,
  LoginRegisterFormValues,
  OpenLoginRegisterPopupOptions,
} from "./LoginRegisterPopup.types";
import "./LoginRegisterPopup.scss";

type AuthMode = "login" | "register";

export function LoginRegisterPopup({
  className = "",
}: LoginRegisterPopupProps) {
  const t = useT();
  const navigate = useNavigation();
  const { login, register, loginLoading, registerLoading } = useUserAuth();
  const { enableWebPush } = useStartupDataFlattened();
  const isAuthenticated = useUserStore(state => state.isAuthenticated());

  const loginRegisterConfig = useLoginRegisterConfig();
  const resetPasswordIpInCooldown = useResetPasswordIpInCooldown();
  const resetPasswordEmailIpInCooldown = useResetPasswordEmailIpInCooldown();
  const onlineServiceSupportUrl = useOnlineServiceSupportUrl();

  const homeRegisterPopupBanner = useHomeDisplayStore(
    s => s.data?.platformLogo?.homeRegisterPopupBanner
  );
  // 头图只要后台上传了就展示，与「进入首页自动弹注册弹窗」开关
  // （homeRegisterPopupEnabled，见 HomePage.tsx）无关
  const hasBanner = !!homeRegisterPopupBanner?.trim();

  const [visible, setVisible] = useState(false);
  const [fromPath, setFromPath] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [authMode, setAuthMode] = useState<AuthMode>("login");
  const [loginMethod, setLoginMethod] = useState<"sms" | "password">(
    "password"
  );
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
  const [wafToken, setWafToken] = useState<string>("");
  const [imageCode, setImageCode] = useState<string>("");
  const [selectedCountryId, setSelectedCountryId] = useState<string>(
    utils.getObject("countryId")
  );
  const [selectedPhoneCode, setSelectedPhoneCode] = useState<string>("");
  const [protocolDialogVisible, setProtocolDialogVisible] = useState(false);
  const [currentProtocolPath, setCurrentProtocolPath] = useState("");
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);
  const [inviteCode, setInviteCode] = useState<string>("");
  const [resetPasswordByEmailVisible, setResetPasswordByEmailVisible] =
    useState(false);
  const [passwordRecoveryVisible, setPasswordRecoveryVisible] = useState(false);
  const [tooManyAttemptsVisible, setTooManyAttemptsVisible] = useState(false);

  const { pause, resume } = usePopupQueue();
  const wasAutoTriggeredRef = useRef(false);
  // 使用ref存储pause和resume，避免作为useEffect依赖项导致监听器被意外释放
  const pauseRef = useRef(pause);
  const resumeRef = useRef(resume);
  pauseRef.current = pause;
  resumeRef.current = resume;

  const { timeLeft, isActive, start, reset } = useCountdown(60);
  const {
    timeLeft: registerTimeLeft,
    isActive: registerIsActive,
    start: registerStart,
    reset: registerReset,
  } = useCountdown(60);
  const [registerSmsLoading, setRegisterSmsLoading] = useState(false);
  const { enableWafCode, wafSiteKey, formKey, setAuthConfig } =
    useAuthConfigStore();

  const enableLoginMobileVerifyCode =
    loginRegisterConfig?.enableLoginMobileVerifyCode ?? false;
  const enableLoginImageCode =
    loginRegisterConfig?.enableLoginImageCode ?? false;
  const enableProtocolText =
    loginRegisterConfig?.enableDefaultCheckProtocol ?? false;
  const defaultProtocolText =
    loginRegisterConfig?.defaultProtocolText ??
    "I confirm that I am 21 years old and I have read the [Terms of use](/protocol/terms) and [Privacy policy](/protocol/privacy)";
  const registerPromoText =
    loginRegisterConfig?.registerFirstChargePromoText?.trim() ?? "";
  const enableSecondPasswordConfirm =
    loginRegisterConfig?.enableSecondPasswordConfirm ?? true;
  const enableRegisterMobileVerify =
    loginRegisterConfig?.enableRegisterMobileVerify ?? false;

  const isLoading = loginLoading || registerLoading;

  const parseMarkdownLinks = (text: string): string =>
    text.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="javascript:void(0)" data-protocol-path="$2">$1</a>'
    );
  const protocolHtml = parseMarkdownLinks(defaultProtocolText);

  const handleProtocolLinkClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "A" && target.hasAttribute("data-protocol-path")) {
      e.preventDefault();
      const path = target.getAttribute("data-protocol-path");
      if (path) {
        setCurrentProtocolPath(path);
        setProtocolDialogVisible(true);
      }
    }
  };

  // Show tabs when enableLoginMobileVerifyCode is true (for login mode only)
  const showTabs = enableLoginMobileVerifyCode && authMode === "login";

  const phonePattern = useMemo(
    () => getPhonePattern(selectedCountryId),
    [selectedCountryId]
  );

  useEffect(() => {
    const countryId = utils.getObject("countryId");
    const country = window._config?.countryList?.find(c => c.id === countryId);
    if (country && !selectedPhoneCode) {
      setSelectedPhoneCode(country.phoneCode);
      setSelectedCountryId(country.id);
    }
  }, [selectedPhoneCode]);

  useEffect(() => {
    if (!visible) return;
    const loadAuthConfig = async () => {
      try {
        const response = await getAuthConfig();
        if (response) {
          setAuthConfig({
            enableWafCode: response.enableWafCode || false,
            wafSiteKey: response.wafSiteKey || "",
            formKey: response.formKey || "",
            disablePhone: response.disablePhone || false,
          });
        }
      } catch {
        // ignore
      }
    };
    loadAuthConfig();
  }, [visible, setAuthConfig]);

  useEffect(() => {
    if (visible) {
      const searchParams = new URLSearchParams(location.search);
      const pParam = searchParams.get("p") || utils.getObject("invitedCode");
      if (pParam) setInviteCode(pParam);
    }
  }, [visible]);

  useEffect(() => {
    console.log(
      "[LoginRegisterPopup] useEffect mounting, adding event listeners"
    );
    const applyOpen = (detail?: OpenLoginRegisterPopupOptions) => {
      console.log("[LoginRegisterPopup] applyOpen called with detail:", detail);
      // Set auth mode based on defaultMode
      const mode: AuthMode =
        detail?.defaultMode === "register" ? "register" : "login";
      setAuthMode(mode);
      if (detail?.from) setFromPath(detail.from);
      setVisible(true);
      if (detail?.isAutoTrigger) {
        pauseRef.current();
        wasAutoTriggeredRef.current = true;
      }
    };
    const handleOpen = (event: Event) => {
      console.log("[LoginRegisterPopup] handleOpen event received");
      applyOpen((event as CustomEvent<OpenLoginRegisterPopupOptions>).detail);
    };
    const handleClose = () => {
      setVisible(false);
      if (wasAutoTriggeredRef.current) {
        resumeRef.current();
        wasAutoTriggeredRef.current = false;
      }
    };

    window.addEventListener(LOGIN_REGISTER_POPUP_EVENT, handleOpen);
    window.addEventListener(CLOSE_LOGIN_REGISTER_POPUP_EVENT, handleClose);
    // 消费监听器挂载前缓存的 open 请求（如首页自动触发早于本组件 commit）
    const pending = consumePendingLoginRegisterPopup();
    if (pending) applyOpen(pending);
    return () => {
      console.log(
        "[LoginRegisterPopup] useEffect cleanup, removing event listeners"
      );
      releaseLoginRegisterPopupListener();
      window.removeEventListener(LOGIN_REGISTER_POPUP_EVENT, handleOpen);
      window.removeEventListener(CLOSE_LOGIN_REGISTER_POPUP_EVENT, handleClose);
    };
  }, []); // 移除pause和resume依赖，使用ref代替

  useEffect(() => {
    if (isAuthenticated && visible) {
      setVisible(false);
      if (wasAutoTriggeredRef.current) {
        resume();
        wasAutoTriggeredRef.current = false;
      }
    }
  }, [isAuthenticated, visible, resume]);

  // PC端：弹窗打开时给 html 添加类，用于降低浮动元素层级
  useEffect(() => {
    if (visible) {
      document.documentElement.classList.add("login-register-popup-open");
    } else {
      document.documentElement.classList.remove("login-register-popup-open");
    }
    return () => {
      document.documentElement.classList.remove("login-register-popup-open");
    };
  }, [visible]);

  const handleFieldsChange = useCallback(() => {
    setTimeout(() => {
      const values = form.getFieldsValue();
      const errors = form.getFieldsError();
      const hasErrors = errors.some(
        (field: { errors: string[] }) => field.errors.length > 0
      );
      let hasEmptyFields = false;
      if (authMode === "register") {
        const baseMissing = !values.mobile || !values.password;
        const confirmMissing =
          enableSecondPasswordConfirm && !values.confirmPassword;
        const codeMissing = enableRegisterMobileVerify && !values.code;
        hasEmptyFields = baseMissing || confirmMissing || codeMissing;
      } else {
        // Login mode
        if (loginMethod === "sms") {
          hasEmptyFields = !values.mobile || !values.code;
        } else {
          hasEmptyFields = !values.mobile || !values.password;
        }
      }
      // Protocol agreement is only required for register mode
      const protocolOk = authMode === "login" || values.agreeTerms === true;
      setIsFormValid(!hasErrors && !hasEmptyFields && protocolOk);
      const errorMap: Record<string, boolean> = {};
      errors.forEach(field => {
        errorMap[String(field.name[0])] = field.errors.length > 0;
      });
      setFormErrors(errorMap);
    }, 0);
  }, [
    form,
    authMode,
    loginMethod,
    enableSecondPasswordConfirm,
    enableRegisterMobileVerify,
  ]);

  const handleGetSmsCode = async () => {
    const mobile = form.getFieldValue("mobile");
    if (!mobile || !validatePhone(mobile, selectedCountryId).valid) {
      form.setFields([
        { name: "mobile", errors: [t("auth.pleaseEnterValidMobile")] },
      ]);
      return;
    }
    setSmsLoading(true);
    try {
      const processed = processPhone(mobile);
      const res = await sendSmsCode(processed, "login", selectedCountryId);
      if (res.status === 200) {
        reset();
        start();
      } else {
        form.setFields([
          { name: "mobile", errors: [res.message || t("auth.sendCodeFailed")] },
        ]);
      }
    } catch {
      form.setFields([{ name: "mobile", errors: [t("auth.sendCodeFailed")] }]);
    } finally {
      setSmsLoading(false);
    }
  };

  const handleGetRegisterSmsCode = async () => {
    const mobile = form.getFieldValue("mobile");
    if (!mobile || !validatePhone(mobile, selectedCountryId).valid) {
      form.setFields([
        { name: "mobile", errors: [t("auth.pleaseEnterValidMobile")] },
      ]);
      return;
    }
    setRegisterSmsLoading(true);
    try {
      const processed = processPhone(mobile);
      const res = await sendSmsCode(processed, "register", selectedCountryId);
      if (res.status === 200) {
        registerReset();
        registerStart();
      } else {
        form.setFields([
          { name: "mobile", errors: [res.message || t("auth.sendCodeFailed")] },
        ]);
      }
    } catch {
      form.setFields([{ name: "mobile", errors: [t("auth.sendCodeFailed")] }]);
    } finally {
      setRegisterSmsLoading(false);
    }
  };

  const handleAuthSuccess = async (
    user: import("../../modules/user/types").User,
    extra?: { pixelEvent?: unknown },
    isRegister = false
  ) => {
    if (isRegister) {
      AdUtils.onRegister(extra?.pixelEvent);
      utils.setObject("invitedCode", null);
      utils.setObject("has_registered", null);
      utils.setObject("__auuid", null);
      utils.setObject("__uid", null);
      document.cookie = "__auuid=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
      document.cookie = "__uid=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
    } else {
      AdUtils.onLogin();
    }

    const userId = user.userId || utils.getObject("user")?.userId;
    trackingService.trackLoginSuccess(userId).catch(() => {});
    utils.setObject("has_registered", null);

    try {
      await wsUtils.open("global");
    } catch {
      // ignore
    }

    if (enableWebPush && isWebPushSupported()) {
      try {
        const { permission } = await getPushSubscriptionStatus();
        if (permission === "granted") subscribeHasLogin();
      } catch {
        // ignore
      }
    }

    setVisible(false);
    form.resetFields();
    setImageCode("");

    if (wasAutoTriggeredRef.current) {
      resume();
      wasAutoTriggeredRef.current = false;
    }

    if (fromPath) {
      navigate.navigate(fromPath, { replace: true });
      setFromPath(null);
    }
  };

  const doAuth = async (values: LoginRegisterFormValues) => {
    try {
      if (authMode === "login") {
        trackingService.trackLoginStart().catch(() => {});
      }

      if (enableWafCode && !wafToken) {
        Toast.failed(t("auth.pleaseCompleteVerification"));
        return;
      }

      const deviceId = utils.getDeviceId();

      let response;

      if (authMode === "register") {
        // 注册固定走手机号：传 phone，不能把电话当 username（后端会 forbidden）
        const confirmPwd = enableSecondPasswordConfirm
          ? values.confirmPassword!
          : values.password!;
        const registerData: RegisterRequest & { deviceId?: string } = {
          phone: processPhone(values.mobile!),
          password: values.password!,
          confirmPassword: confirmPwd,
          inviteCode: inviteCode || undefined,
          countryId: selectedCountryId,
          wafCode: enableWafCode ? wafToken : undefined,
          imageCode: enableLoginImageCode ? imageCode : undefined,
          code: enableRegisterMobileVerify ? values.code : undefined,
        };
        if (deviceId) registerData.deviceId = deviceId;
        response = await register(registerData);

        if (response?.user) {
          await handleAuthSuccess(response.user, response.extra, true);
        }
      } else {
        // Login mode - call login API
        if (loginMethod === "sms") {
          // SMS login
          const processed = processPhone(values.mobile!);
          const loginData: LoginRequest & {
            deviceId?: string;
            imageCode?: string;
            countryId?: string;
          } = {
            phone: processed,
            code: values.code!,
            countryId: selectedCountryId,
            remember: true,
            loginType: "mobile" as const,
            wafCode: enableWafCode ? wafToken : undefined,
            imageCode: enableLoginImageCode ? imageCode : undefined,
          };
          if (deviceId) loginData.deviceId = deviceId;
          response = await login(loginData);
        } else {
          // Password login：账号仍走 username（可为手机/邮箱/用户ID）
          const processedUsername = values.mobile
            ? values.mobile.replace(/^0+/, "")
            : values.mobile;
          const loginData: LoginRequest & {
            deviceId?: string;
            imageCode?: string;
          } = {
            username: processedUsername!,
            password: values.password!,
            remember: true,
            loginType: "username" as const,
            wafCode: enableWafCode ? wafToken : undefined,
            imageCode: enableLoginImageCode ? imageCode : undefined,
          };
          if (deviceId) loginData.deviceId = deviceId;
          response = await login(loginData);
        }

        if (response?.user) {
          await handleAuthSuccess(response.user, response.extra, false);
        } else {
          trackingService.trackLoginFailed("Login failed").catch(() => {});
        }
      }
    } catch (error) {
      const errorMsg =
        error instanceof Error
          ? error.message
          : t(
              authMode === "register"
                ? "auth.registerFailed"
                : "auth.loginFailed"
            );
      if (authMode === "login") {
        trackingService.trackLoginFailed(errorMsg).catch(() => {});
      }
      Toast.failed(errorMsg);
    }
  };

  const handleSubmit = (values: LoginRegisterFormValues) => {
    triggerAgreementConfirmation(() => doAuth(values), undefined);
  };

  const handleClose = () => {
    setVisible(false);
    setFromPath(null);
    form.resetFields();
    setImageCode("");
    if (wasAutoTriggeredRef.current) {
      resume();
      wasAutoTriggeredRef.current = false;
    }
  };

  const handleHeaderRightIconClick = () => {
    handleClose();
    navigate.navigate(ROUTES.MESSAGE_CENTER);
  };

  const handleTabChange = (key: string) => {
    setLoginMethod(key as "sms" | "password");
    form.resetFields();
    setImageCode("");
    setIsFormValid(false);
    setFormErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
    reset();
  };

  const handleModeSwitch = () => {
    setAuthMode(authMode === "login" ? "register" : "login");
    setLoginMethod("password");
    form.resetFields();
    setImageCode("");
    setIsFormValid(false);
    setFormErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
    reset();
    registerReset();
  };

  return (
    <>
      <AntdMobilePopup
        visible={visible}
        onClose={handleClose}
        position="right"
        className={`login-register-popup ${className}`}
        bodyClassName="login-register-popup__body"
        maskClassName="login-register-popup__mask"
        bodyStyle={{ width: "100vw" }}
        closeOnSwipe={false}
        destroyOnClose
        getContainer={() =>
          document.querySelector(".phone-wrapper") || document.body
        }
      >
        {/* Header area — like subpage-header */}
        <div className="login-register-popup__header">
          <button
            className="login-register-popup__header-back-btn"
            onClick={handleClose}
            aria-label="Go back"
          >
            <img
              src={cdnAsset_BbVwPaWC}
              alt=""
              className="login-register-popup__header-back-icon"
              draggable={false}
            />
          </button>
          <span className="login-register-popup__header-title">
            {t(authMode === "register" ? "auth.register" : "auth.login")}
          </span>
          <img
            src={cdnAsset_C29CXa4K}
            alt=""
            className="login-register-popup__header-right-icon"
            draggable={false}
            onClick={handleHeaderRightIconClick}
            role="button"
          />
        </div>

        {/* Content area with subpage-bg style */}
        <div className="login-register-popup__content subpage-bg">
          {/* Banner / background layer */}
          <div
            className={`login-register-popup__banner${hasBanner ? " login-register-popup__banner--image" : " login-register-popup__banner--none"}`}
          >
            {hasBanner && homeRegisterPopupBanner && (
              <img
                src={utils.getImageUrl(homeRegisterPopupBanner)}
                alt="register banner"
                className="login-register-popup__banner-img"
                draggable={false}
              />
            )}
          </div>

          {/* Scrollable form area (elevated above banner) */}
          <div
            className={`login-register-popup__scroll${hasBanner ? " login-register-popup__scroll--elevated" : ""} ${!hasBanner ? " login-register-popup__scroll--banner--none" : ""}`}
          >
            {/* Tabs: SMS / Password (only show when enableLoginMobileVerifyCode is true and in login mode) */}
            {showTabs && (
              <div className="login-register-popup__tabs">
                <Tabs
                  activeKey={loginMethod}
                  align="left"
                  onChange={handleTabChange}
                >
                  <Tab key="sms" title={t("auth.smsLogin")} />
                  <Tab key="password" title={t("auth.passwordLogin")} />
                </Tabs>
              </div>
            )}

            {/* Form */}
            <div className="login-register-popup__form">
              <Form
                form={form}
                onFinish={handleSubmit}
                onFieldsChange={handleFieldsChange}
                layout="vertical"
                className="login-register-form"
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  {/* SMS Login mode - Phone number with country code */}
                  {authMode === "login" && loginMethod === "sms" && (
                    <>
                      <FormItem
                        name="mobile"
                        rules={[
                          {
                            required: true,
                            message:
                              t("auth.pleaseEnterValidPhone") ||
                              t("auth.pleaseEnterValidMobile") ||
                              "Please enter a valid phone number",
                          },
                          {
                            pattern: phonePattern,
                            message:
                              t("auth.pleaseEnterValidMobile") ||
                              "Invalid phone number format",
                          },
                        ]}
                      >
                        <EnhancedInput
                          placeholder={t("auth.mobile") || "Phone number"}
                          autoComplete="tel"
                          prefixIcon={{
                            icon: (
                              <div className="login-register-popup__phone-prefix">
                                <CountryCodeSelect
                                  size="md"
                                  value={selectedCountryId}
                                  onChange={(countryId, phoneCode) => {
                                    setSelectedCountryId(countryId);
                                    setSelectedPhoneCode(phoneCode);
                                  }}
                                />
                                <span className="login-register-popup__phone-divider" />
                              </div>
                            ),
                          }}
                          error={formErrors.mobile}
                        />
                      </FormItem>
                      <FormItem
                        name="code"
                        rules={[
                          {
                            required: true,
                            message:
                              t("auth.pleaseEnterCode") ||
                              "Please enter verification code",
                          },
                        ]}
                      >
                        <EnhancedInput
                          placeholder={
                            t("auth.verificationCode") || "Verification code"
                          }
                          suffixContent={{
                            text: isActive
                              ? `${timeLeft}s`
                              : t("common.getCode") || "Get Code",
                            onClick:
                              isActive || smsLoading
                                ? undefined
                                : handleGetSmsCode,
                            disabled: isActive || smsLoading,
                          }}
                          error={formErrors.code}
                        />
                      </FormItem>
                    </>
                  )}

                  {/* Password Login mode or Register mode - Account and password fields */}
                  {(authMode === "register" ||
                    (authMode === "login" && loginMethod === "password")) && (
                    <>
                      <FormItem
                        name="mobile"
                        rules={
                          authMode === "register"
                            ? [
                                {
                                  required: true,
                                  message:
                                    t("auth.pleaseEnterMobile") ||
                                    t("auth.pleaseEnterPhone"),
                                },
                                {
                                  validator: (_, value) => {
                                    if (!value) return Promise.resolve();
                                    // 与注册页一致：区号 + 号码一起校验
                                    if (!selectedPhoneCode) {
                                      return Promise.resolve();
                                    }
                                    const fullPhone = `${selectedPhoneCode}${value}`;
                                    if (!phonePattern.test(fullPhone)) {
                                      return Promise.reject(
                                        new Error(
                                          t("auth.pleaseEnterValidMobile") ||
                                            t("auth.pleaseEnterValidPhone")
                                        )
                                      );
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]
                            : [
                                {
                                  required: true,
                                  message:
                                    t("auth.pleaseEnterAccount") ||
                                    "Please enter your account",
                                },
                              ]
                        }
                      >
                        <EnhancedInput
                          placeholder={
                            authMode === "register"
                              ? t("auth.mobile") || t("auth.phone")
                              : t("auth.loginPlaceholder") ||
                                "Phone / Email / User ID"
                          }
                          autoComplete={
                            authMode === "register" ? "tel" : "username"
                          }
                          prefixIcon={
                            authMode === "register"
                              ? {
                                  icon: (
                                    <div className="login-register-popup__phone-prefix">
                                      <CountryCodeSelect
                                        size="md"
                                        value={selectedCountryId}
                                        onChange={(countryId, phoneCode) => {
                                          setSelectedCountryId(countryId);
                                          setSelectedPhoneCode(phoneCode);
                                          if (authMode === "register") {
                                            form.validateFields(["mobile"]);
                                          }
                                        }}
                                      />
                                      <span className="login-register-popup__phone-divider" />
                                    </div>
                                  ),
                                }
                              : undefined
                          }
                          error={formErrors.mobile}
                        />
                      </FormItem>

                      {/* Register SMS verification code - only for register when enableRegisterMobileVerify is true */}
                      {authMode === "register" &&
                        enableRegisterMobileVerify && (
                          <FormItem
                            name="code"
                            rules={[
                              {
                                required: true,
                                message:
                                  t("auth.pleaseEnterCode") ||
                                  "Please enter verification code",
                              },
                            ]}
                          >
                            <EnhancedInput
                              placeholder={
                                t("auth.verificationCode") ||
                                "Verification code"
                              }
                              suffixContent={{
                                text: registerIsActive
                                  ? `${registerTimeLeft}s`
                                  : t("common.getCode") || "Get Code",
                                onClick:
                                  registerIsActive || registerSmsLoading
                                    ? undefined
                                    : handleGetRegisterSmsCode,
                                disabled:
                                  registerIsActive || registerSmsLoading,
                              }}
                              error={formErrors.code}
                            />
                          </FormItem>
                        )}

                      <FormItem
                        name="password"
                        rules={[
                          {
                            required: true,
                            message: t("auth.pleaseEnterPassword"),
                          },
                          { min: 8, message: t("auth.passwordTooShort") },
                        ]}
                      >
                        <EnhancedInput
                          placeholder={t("auth.password")}
                          type={showPassword ? "text" : "password"}
                          autoComplete={
                            authMode === "register"
                              ? "new-password"
                              : "current-password"
                          }
                          suffixContent={{
                            icon: showPassword ? "eye" : "eye-closed",
                            onClick: () => setShowPassword(!showPassword),
                          }}
                          error={formErrors.password}
                        />
                      </FormItem>

                      {/* Confirm password field - only for register when enableSecondPasswordConfirm is true */}
                      {authMode === "register" &&
                        enableSecondPasswordConfirm && (
                          <FormItem
                            name="confirmPassword"
                            rules={[
                              {
                                required: true,
                                message:
                                  t("auth.pleaseConfirmPassword") ||
                                  "Please confirm your password",
                              },
                              {
                                validator: (_, value) => {
                                  const password =
                                    form.getFieldValue("password");
                                  if (value && password && value !== password) {
                                    return Promise.reject(
                                      new Error(
                                        t("auth.passwordMismatch") ||
                                          "Passwords do not match"
                                      )
                                    );
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                          >
                            <EnhancedInput
                              placeholder={
                                t("auth.confirmPassword") || "Confirm Password"
                              }
                              type={showConfirmPassword ? "text" : "password"}
                              autoComplete="new-password"
                              suffixContent={{
                                icon: showConfirmPassword
                                  ? "eye"
                                  : "eye-closed",
                                onClick: () =>
                                  setShowConfirmPassword(!showConfirmPassword),
                              }}
                              error={formErrors.confirmPassword}
                            />
                          </FormItem>
                        )}
                    </>
                  )}

                  {/* Forgot password - only for password login */}
                  {authMode === "login" &&
                    loginMethod === "password" &&
                    (loginRegisterConfig?.enableMobileResetPassword ||
                      loginRegisterConfig?.enableEmailResetPassword) && (
                      <div className="login-register-popup__forgot-row">
                        <AntdLink
                          variant="primary"
                          underline
                          onClick={() => {
                            const phoneOn =
                              !!loginRegisterConfig?.enableMobileResetPassword;
                            const emailOn =
                              !!loginRegisterConfig?.enableEmailResetPassword;
                            if (phoneOn && emailOn) {
                              setPasswordRecoveryVisible(true);
                            } else if (phoneOn) {
                              if (resetPasswordIpInCooldown) {
                                setTooManyAttemptsVisible(true);
                              } else {
                                setResetPasswordVisible(true);
                              }
                            } else {
                              if (resetPasswordEmailIpInCooldown) {
                                setTooManyAttemptsVisible(true);
                              } else {
                                setResetPasswordByEmailVisible(true);
                              }
                            }
                          }}
                          className="login-register-popup__forgot-link"
                        >
                          {t("auth.forgotPassword") || "Forgot?"}
                        </AntdLink>
                      </div>
                    )}

                  {/* Image captcha */}
                  {enableLoginImageCode && (
                    <FormItem name="imageCode">
                      <ImageCode
                        value={imageCode}
                        onChange={setImageCode}
                        error={!!formErrors.imageCode}
                      />
                    </FormItem>
                  )}

                  {/* Turnstile WAF */}
                  {enableWafCode && wafSiteKey && (
                    <div className="login-register-popup__turnstile">
                      <Turnstile
                        key={formKey}
                        siteKey={wafSiteKey}
                        onChange={token => setWafToken(token)}
                      />
                    </div>
                  )}

                  {/* Remember password - only for login */}
                  {authMode === "login" && (
                    <div className="login-register-popup__remember">
                      <FormItem
                        name="remember"
                        valuePropName="checked"
                        initialValue={true}
                      >
                        <Checkbox variant="golden">
                          <span className="login-register-popup__remember-text">
                            {t("auth.rememberPassword") ||
                              "Recordar contraseña"}
                          </span>
                        </Checkbox>
                      </FormItem>
                    </div>
                  )}

                  {/* Submit */}
                  <div className="login-register-popup__submit-btn-wrap">
                    {authMode === "register" && registerPromoText && (
                      <div className="login-register-popup__promo-bubble">
                        <span className="login-register-popup__promo-icon-wrap">
                          <span className="login-register-popup__promo-bg" />
                          <img
                            src={cdnAsset_RXDuDN1V}
                            alt=""
                            className="login-register-popup__promo-icon"
                            draggable={false}
                          />
                        </span>
                        <span className="login-register-popup__promo-text">
                          {registerPromoText}
                        </span>
                      </div>
                    )}
                    <Button
                      type="submit"
                      block
                      size="large"
                      variant="primary"
                      loading={isLoading}
                      disabled={
                        !isFormValid ||
                        isLoading ||
                        (enableWafCode && !wafToken)
                      }
                      className="login-register-popup__submit-btn"
                    >
                      {t(
                        authMode === "register" ? "auth.register" : "auth.login"
                      )}
                    </Button>
                  </div>

                  {/* Terms - only for register */}
                  {authMode === "register" && (
                    <div className="login-register-popup__terms">
                      <FormItem
                        name="agreeTerms"
                        valuePropName="checked"
                        initialValue={enableProtocolText}
                        rules={[
                          {
                            validator: (_, value) =>
                              value
                                ? Promise.resolve()
                                : Promise.reject(
                                    new Error(t("auth.agreeTermsRequired"))
                                  ),
                          },
                        ]}
                      >
                        <Checkbox variant="golden">
                          <span
                            className="login-register-popup__terms-text"
                            dangerouslySetInnerHTML={{ __html: protocolHtml }}
                            onClick={handleProtocolLinkClick}
                          />
                        </Checkbox>
                      </FormItem>
                    </div>
                  )}

                  {/* Switch mode link */}
                  <div className="login-register-popup__switch-mode">
                    {authMode === "login" ? (
                      <p className="login-register-popup__switch-mode-text">
                        {t("auth.noAccountYet")}{" "}
                        <span
                          className="login-register-popup__switch-mode-link"
                          onClick={handleModeSwitch}
                        >
                          {t("auth.register")}
                        </span>
                      </p>
                    ) : (
                      <p className="login-register-popup__switch-mode-text">
                        {t("auth.alreadyHaveAccount")}{" "}
                        <span
                          className="login-register-popup__switch-mode-link"
                          onClick={handleModeSwitch}
                        >
                          {t("auth.login")}
                        </span>
                      </p>
                    )}
                  </div>
                </Space>
              </Form>
            </div>
          </div>
        </div>
      </AntdMobilePopup>

      {/* Protocol Dialog */}
      <ProtocolDialog
        visible={protocolDialogVisible}
        onClose={() => setProtocolDialogVisible(false)}
        protocolPath={currentProtocolPath}
      />

      {/* Password Recovery Dialogs */}
      {loginRegisterConfig?.enableMobileResetPassword &&
        loginRegisterConfig?.enableEmailResetPassword && (
          <PasswordRecoveryDialog
            visible={passwordRecoveryVisible}
            onClose={() => setPasswordRecoveryVisible(false)}
            onSelectMethod={method => {
              setPasswordRecoveryVisible(false);
              if (method === "phone") {
                if (resetPasswordIpInCooldown) {
                  setTooManyAttemptsVisible(true);
                } else {
                  setResetPasswordVisible(true);
                }
              } else {
                if (resetPasswordEmailIpInCooldown) {
                  setTooManyAttemptsVisible(true);
                } else {
                  setResetPasswordByEmailVisible(true);
                }
              }
            }}
          />
        )}

      {loginRegisterConfig?.enableMobileResetPassword && (
        <ResetPasswordDialog
          visible={resetPasswordVisible}
          onClose={() => setResetPasswordVisible(false)}
          onSuccess={() => {
            setResetPasswordVisible(false);
            setPasswordRecoveryVisible(false);
          }}
          resetMethods={loginRegisterConfig?.resetPasswordMethods}
          onTooManyAttempts={() => {
            setPasswordRecoveryVisible(false);
            setTooManyAttemptsVisible(true);
          }}
        />
      )}

      {loginRegisterConfig?.enableEmailResetPassword && (
        <ResetPasswordByEmailDialog
          visible={resetPasswordByEmailVisible}
          onClose={() => setResetPasswordByEmailVisible(false)}
          onSuccess={() => {
            setResetPasswordByEmailVisible(false);
            setPasswordRecoveryVisible(false);
          }}
          onTooManyAttempts={() => {
            setPasswordRecoveryVisible(false);
            setTooManyAttemptsVisible(true);
          }}
        />
      )}

      <TooManyPasswordAttemptsDialog
        visible={tooManyAttemptsVisible}
        onClose={() => {
          setTooManyAttemptsVisible(false);
          useHomeDisplayStore.getState().refreshConfig();
        }}
        supportUrl={onlineServiceSupportUrl}
      />
    </>
  );
}

export default LoginRegisterPopup;
